import React from "react";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import { useSiteContent } from "@/context/SiteContentContext";
import { resolveAssetUrl } from "@/lib/api";

const SECTIONS = [
  "Breakfast", "Teas & Chocolate", "Quick Fix", "Salads",
  "Sandwiches & Wraps", "Fish & Chicken", "Goat & Beef",
  "Signature Platters", "Sides & Starches", "Vegetables",
  "Juices", "Soda & Water", "Soups", "Desserts",
];

export default function Footer() {
  const { content } = useSiteContent();
  const brand = content?.brand || {};
  const footer = content?.footer || {};
  const marquee = footer.marquee_lines || [];
  const year = new Date().getFullYear();
  const jump = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <footer data-testid="site-footer" className="relative bg-bg border-t border-warm">
      <div className="overflow-hidden border-b border-warm py-6">
        <div className="marquee-track flex gap-16 whitespace-nowrap font-serif-display italic text-3xl md:text-5xl text-[color:var(--gold-lo)]/70">
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex gap-16">
              {marquee.map((m, idx) => (
                <span key={idx}>{m}</span>
              ))}
              {marquee.slice(0, 2).map((m, idx) => (
                <span key={`dup-${idx}`}>{m}</span>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3">
            <img src={resolveAssetUrl(brand.logo_url)} alt={brand.name} className="w-12 h-12 rounded-full object-cover ring-1 ring-[color:var(--gold)]/40" />
            <div>
              <div className="font-serif-display text-2xl text-[color:var(--gold-hi)]">{brand.name}</div>
              <div className="text-[10px] tracking-[0.4em] uppercase text-muted-warm">Restaurant</div>
            </div>
          </div>
          <p className="mt-6 text-cream/75 max-w-sm leading-relaxed" data-testid="footer-tagline">
            {footer.tagline}
          </p>
        </div>

        <div className="md:col-span-3">
          <div className="text-xs tracking-[0.35em] uppercase text-muted-warm mb-4">Explore</div>
          <ul className="space-y-2">
            {["story", "menu", "grill", "visit"].map((id) => (
              <li key={id}>
                <button onClick={() => jump(id)} data-testid={`footer-link-${id}`} className="text-cream/85 hover:text-[color:var(--gold-hi)] font-serif-display text-lg">
                  {id === "visit" ? "Visit Us" : id === "menu" ? "The Menu" : id === "story" ? "The Story" : "The Grill"}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="text-xs tracking-[0.35em] uppercase text-muted-warm mb-4">Sections</div>
          <div className="flex flex-wrap gap-2">
            {SECTIONS.map((name) => (
              <span key={name} className="text-xs uppercase tracking-[0.15em] px-3 py-1.5 rounded-full border border-warm text-cream/70">
                {name}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="border-t border-warm">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs tracking-[0.2em] uppercase text-muted-warm">
          <span>© {year} {footer.copyright}</span>
          <span className="text-center">{footer.location_line}</span>
          <Link
            to="/admin/login"
            data-testid="footer-admin-link"
            className="inline-flex items-center gap-2 rounded-full border border-warm px-4 py-2 text-[10px] tracking-[0.3em] uppercase text-cream/60 hover:text-[color:var(--gold-hi)] hover:border-[color:var(--gold)] transition"
          >
            <Lock size={11} /> Admin
          </Link>
        </div>
      </div>
    </footer>
  );
}
